import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Transaction } from '../../../../models/User.tsx';
import {
    deleteTransaction,
    showUserTransaccion,
    UserTransactionDays,
} from '../../../../services/admin/Transaction.tsx';
import { formatNumbers } from '../../../../helpers/functions.tsx';
import { formatAdminFiguresDateTime } from '../../../../helpers/adminFiguresTime.tsx';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import { useUser } from '../../../../context/UserContext.tsx';

const SKELETON_ROW_COUNT = 8;

const parseFilterDays = (value: string): UserTransactionDays => {
    if (value === '30') return 30;
    if (value === '90') return 90;
    if (value === 'all') return 'all';
    return 7;
};

const TransactionTableSkeleton = () => (
    <>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
            <tr key={index} className="user-transaction-skeleton-row" aria-hidden="true">
                <td>
                    <span className="rect-placeholder placeholder-transaction-date" />
                    <span className="rect-placeholder placeholder-transaction-id" />
                </td>
                <td colSpan={3}>
                    <span className="rect-placeholder placeholder-transaction-description" />
                </td>
                <td>
                    <span className="rect-placeholder placeholder-transaction-amount" />
                </td>
                <td />
            </tr>
        ))}
    </>
);

const UserTransaction = () => {
    const { user } = useParams<{ user: string }>();
    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();

    const [formData, setFormData] = useState<Transaction[] | null>(null);
    const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
    const [filter, setFilter] = useState('7');
    const [transactionType, setTransactionType] = useState<string>('all');
    const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadTransactions = useCallback(async (days: UserTransactionDays) => {
        if (!user) return;

        setFormData(null);
        setIsLoading(true);
        try {
            const result = await showUserTransaccion(user, days);
            setFormData(result);
        } catch (error) {
            console.error('Error', error);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedFilter = e.target.value;
        setFilter(selectedFilter);
        setSelectedTransactionId(null);
        loadTransactions(parseFilterDays(selectedFilter));
    };

    const handleTransactionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTransactionType(e.target.value);
        setSelectedTransactionId(null);
    };

    const applyTransactionTypeFilter = useCallback(() => {
        if (!formData) {
            setFilteredTransactions([]);
            return;
        }

        let filtered = [...formData];

        if (transactionType !== 'all') {
            filtered = filtered.filter((transaction) => {
                if (transactionType === 'depo/with') {
                    return (
                        (transaction.transactionType === 'deposit' ||
                            transaction.transactionType === 'withdrawal') &&
                        transaction.status !== 'Deleted'
                    );
                }
                if (transactionType === 'delet') {
                    return transaction.status === 'Deleted';
                }
                return true;
            });
        } else {
            filtered = filtered.filter(transaction => transaction.status !== 'Deleted');
        }

        filtered.sort((a, b) => {
            const dateA = a.createDate?.toDate?.().getTime() ?? 0;
            const dateB = b.createDate?.toDate?.().getTime() ?? 0;
            return dateB - dateA;
        });

        setFilteredTransactions(filtered);
    }, [formData, transactionType]);

    const handleRowSelection = (transactionId: string) => {
        setSelectedTransactionId(transactionId);
    };

    const handleDeleteTransaction = async () => {
        if (!selectedTransactionId || !currentUser?.userName) {
            alert('Please select a transaction to delete.');
            return;
        }
        try {
            await deleteTransaction(selectedTransactionId, currentUser?.userName);
            logUserActivity(
                currentUser!.userName,
                `Transaction deleted: ${selectedTransactionId}`,
                userIpDataAdmin
            );
            alert('Transaction successfully deleted.');
            setFormData(prev =>
                prev ? prev.filter(transaction => transaction.id !== selectedTransactionId) : prev
            );
            setSelectedTransactionId(null);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!user) return;
        setFilter('7');
        setTransactionType('all');
        setSelectedTransactionId(null);
        loadTransactions(7);
    }, [user, loadTransactions]);

    useEffect(() => {
        applyTransactionTypeFilter();
    }, [applyTransactionTypeFilter]);

    return (
        <div className='container-transaction mt-1'>
            <div className='container-option mt-2'>
                <div className='transaction'>
                    <span className='letters-selected'>Display</span>
                    <select
                        className='select-box-admin'
                        value={filter}
                        onChange={handleFilterChange}
                        disabled={isLoading}
                    >
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="all">All</option>
                    </select>
                </div>
                <div className='transaction'>
                    <span className='letters-selected'>Filter Transactions</span>
                    <select
                        className='select-box-admin'
                        value={transactionType}
                        onChange={handleTransactionTypeChange}
                        disabled={isLoading}
                    >
                        <option value="depo/with">Deposit/Withdrawal</option>
                        <option value="all">All Transactions</option>
                        <option value="delet">Deleted</option>
                    </select>
                </div>
            </div>
            <div className='table-responsive' aria-busy={isLoading}>
                <table className="mt-3 table">
                    <thead className="header-customer">
                        <tr>
                            <th>
                                <span>Date (PST)</span>
                            </th>
                            <th colSpan={3}>
                                <span>Description</span>
                            </th>
                            <th>
                                <span>{transactionType === 'delet' ? 'Amount' : 'Amount'}</span>
                            </th>
                            <th>
                                <span>{transactionType === 'delet' ? 'Deleted By' : ''}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="body-customer">
                        {isLoading ? (
                            <TransactionTableSkeleton />
                        ) : (
                            filteredTransactions.map((transaction, index) => {
                                const isNegative =
                                    transaction.description === 'Zero balance debit' ||
                                    transaction.description === 'Wager Lost' ||
                                    transaction.transactionType === 'withdrawal' ||
                                    transaction.transactionType === 'DebitAdjustment';

                                const isPositive =
                                    transaction.description === 'Zero balance credit' ||
                                    transaction.description === 'Wager Won' ||
                                    transaction.transactionType === 'deposit' ||
                                    transaction.transactionType === 'CreditAdjustment';

                                const sign = isNegative ? '-' : '';

                                return (
                                    <tr
                                        key={transaction.id || index}
                                        onClick={
                                            transactionType !== 'delet'
                                                ? () => handleRowSelection(transaction.id!)
                                                : undefined
                                        }
                                        style={{
                                            cursor: transactionType !== 'delet' ? 'pointer' : 'not-allowed',
                                            backgroundColor:
                                                selectedTransactionId === transaction.id
                                                    ? '#ebf584'
                                                    : 'transparent',
                                        }}
                                    >
                                        <td>
                                            <span>
                                                {formatAdminFiguresDateTime(transaction.createDate.toDate(), {
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    year: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: true,
                                                })}
                                            </span>
                                            <span style={{ display: 'block', fontSize: '0.8em', color: '#888' }}>
                                                ID: {transaction.ticket}
                                            </span>
                                        </td>

                                        <td colSpan={3}>
                                            <span>{transaction.description}</span>
                                        </td>

                                        <td>
                                            <span
                                                className={
                                                    isNegative ? 'negative' : isPositive ? 'positive' : ''
                                                }
                                            >
                                                {sign}${formatNumbers(Math.abs(transaction.amount))}
                                            </span>
                                        </td>

                                        <td>
                                            <span>
                                                {transactionType === 'delet'
                                                    ? transaction.deletedBy || 'N/A'
                                                    : ''}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <button
                className='mt-2 transaction-delete'
                onClick={handleDeleteTransaction}
                disabled={isLoading || !selectedTransactionId || transactionType === 'delet'}
            >
                Delete
            </button>
        </div>
    );
};

export default UserTransaction;
